import React, { useState, useEffect, useCallback } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from 'prop-types';
import {
    getTrainingById,
    hasUserPurchasedTraining,
    purchaseTraining,
    getContentTypeLabel,
    getCategoryLabel,
    getContentTypeIcon,
    getCategoryIcon
} from "../../../services/trainingService";
import { auth, getUserProfile } from "../../../config/firebase";
import { createSubscriptionPayment, verifyPaymentStatus } from "../../../services/paymentService";

const TrainingPurchasePage = ({ user, history, match }) => {
    const { id } = match.params;

    // États
    const [training, setTraining] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isPurchased, setIsPurchased] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [error, setError] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState({
        isProcessing: false,
        paymentUrl: null,
        success: false,
        message: null
    });

    // Constante pour l'expiration du paiement en attente
    const PENDING_PAYMENT_EXPIRY = 3600000; // 1 heure en ms

    // Vérifier si la formation est gratuite (0 F CFA)
    const isFreeTraining = training && training.price === 0;

    // Gérer la finalisation de l'achat après paiement réussi
    const handlePaymentSuccess = useCallback(async () => {
        try {
            // Nettoyer le localStorage
            localStorage.removeItem('pendingTrainingPurchase');
            window.history.replaceState({}, document.title, window.location.pathname);

            // Récupérer l'utilisateur actuel
            const currentUser = user?.uid ? user : auth.currentUser;

            if (!currentUser?.uid) {
                throw new Error("Erreur d'authentification. Veuillez vous reconnecter pour finaliser votre achat.");
            }

            // Enregistrer l'achat dans la base de données
            await purchaseTraining(currentUser.uid, training.id, training.price);

            // Mettre à jour l'état
            setPaymentStatus({
                isProcessing: false,
                paymentUrl: null,
                success: true,
                message: "Paiement confirmé ! Votre formation est maintenant disponible."
            });

            // Marquer comme acheté
            setIsPurchased(true);

            // Attendre 2 secondes puis rediriger vers la page de détail
            setTimeout(() => {
                history.push(`/app/training/detail/${id}`);
            }, 2000);

        } catch (error) {
            setPaymentStatus({
                isProcessing: false,
                paymentUrl: null,
                success: false,
                message: `Erreur: ${error.message}`
            });
        }
    }, [user, training, history, id]);

    // Chargement des données initiales
    useEffect(() => {
        const loadInitialData = async () => {
            setIsLoading(true);
            try {
                // Vérifier l'utilisateur connecté
                const currentUser = user?.uid ? user : auth.currentUser;

                if (!currentUser) {
                    setError("Vous devez être connecté pour accéder à cette formation");
                    setIsLoading(false);
                    return;
                }

                // Charger le profil utilisateur
                let profile = null;
                try {
                    profile = await getUserProfile(currentUser.uid);
                } catch (profileError) {
                    // Fallback : utiliser les données de Firebase Auth
                    profile = {
                        name: currentUser.displayName || 'Utilisateur',
                        email: currentUser.email || '',
                        uid: currentUser.uid
                    };
                }

                // S'assurer que l'email est disponible
                if (!profile.email && !currentUser.email) {
                    setError("Impossible de récupérer votre adresse email. Veuillez vous reconnecter.");
                    setIsLoading(false);
                    return;
                }

                // Enrichir le profil
                const enrichedProfile = {
                    ...profile,
                    email: profile.email || currentUser.email,
                    name: profile.name || currentUser.displayName || 'Utilisateur',
                    uid: profile.uid || currentUser.uid
                };

                setUserProfile(enrichedProfile);

                // Charger les détails de la formation
                const trainingData = await getTrainingById(id);
                if (!trainingData) {
                    setError("Formation introuvable");
                    setIsLoading(false);
                    return;
                }

                setTraining(trainingData);

                // Si la formation est gratuite, donner accès direct sans paiement
                if (trainingData.price === 0) {
                    try {
                        // Accès direct aux formations gratuites
                        await purchaseTraining(currentUser.uid, trainingData.id, 0);

                        // Rediriger immédiatement vers la page de détail pour téléchargement
                        history.replace(`/app/training/detail/${id}`);
                        return;
                    } catch (error) {
                        // Continuer avec l'interface normale si l'accès direct échoue
                    }
                }

                // Vérifier si l'utilisateur a déjà acheté cette formation
                const purchased = await hasUserPurchasedTraining(currentUser.uid, id);
                setIsPurchased(purchased);

                // Si la formation est déjà achetée, rediriger vers la page de détail
                if (purchased) {
                    history.replace(`/app/training/detail/${id}`);
                }

            } catch (err) {
                setError("Impossible de charger les informations. Veuillez réessayer.");
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, [id, user, history]);

    // Vérifier les paiements en attente au montage
    useEffect(() => {
        const checkPendingPayment = async () => {
            const pendingData = localStorage.getItem('pendingTrainingPurchase');
            if (!pendingData) return;

            const { trainingId, timestamp } = JSON.parse(pendingData);

            // Vérifier si le trainingId correspond à celui de cette page
            if (trainingId !== id) return;

            // Vérifier si le paiement est expiré
            if (Date.now() - timestamp > PENDING_PAYMENT_EXPIRY) {
                localStorage.removeItem('pendingTrainingPurchase');
                return;
            }

            try {
                const paymentId = new URLSearchParams(window.location.search).get('payment_id');
                if (paymentId) {
                    setPaymentStatus(prev => ({ ...prev, isProcessing: true }));

                    const { status } = await verifyPaymentStatus(paymentId);
                    if (status === 'completed') {
                        await handlePaymentSuccess();
                    } else {
                        setPaymentStatus({
                            isProcessing: false,
                            paymentUrl: null,
                            success: false,
                            message: `Le paiement n'a pas été complété (statut: ${status})`
                        });
                    }
                }
            } catch (error) {
                setPaymentStatus({
                    isProcessing: false,
                    paymentUrl: null,
                    success: false,
                    message: `Erreur de vérification: ${error.message}`
                });
            }
        };

        checkPendingPayment();
    }, [id, handlePaymentSuccess]);

    // Gérer le téléchargement des formations gratuites
    const handleDownloadFreeTraining = async () => {
        if (!training || !userProfile) return;

        setPaymentStatus(prev => ({ ...prev, isProcessing: true, message: null }));

        try {
            // Récupérer l'utilisateur actuel
            const currentUser = user?.uid ? user : auth.currentUser;

            if (!currentUser?.uid) {
                throw new Error("Erreur d'authentification. Veuillez vous reconnecter.");
            }

            // Enregistrer l'accès gratuit dans la base de données
            await purchaseTraining(currentUser.uid, training.id, 0);

            // Mettre à jour l'état
            setPaymentStatus({
                isProcessing: false,
                paymentUrl: null,
                success: true,
                message: "Téléchargement en cours..."
            });

            // Marquer comme acheté
            setIsPurchased(true);

            // Ouvrir le PDF directement s'il existe
            if (training.fileUrl) {
                // Ouvrir le PDF dans un nouvel onglet
                window.open(training.fileUrl, '_blank');

                // Rediriger vers la page de détail après un court délai
                setTimeout(() => {
                    history.push(`/app/training/detail/${id}`);
                }, 1500);
            } else {
                // Si pas de PDF, rediriger directement vers la page de détail
                setTimeout(() => {
                    history.push(`/app/training/detail/${id}`);
                }, 1000);
            }

        } catch (error) {
            setPaymentStatus({
                isProcessing: false,
                paymentUrl: null,
                success: false,
                message: `Erreur lors du téléchargement: ${error.message}`
            });
        }
    };

    // Gérer l'achat des formations payantes
    const handlePurchasePaidTraining = async () => {
        if (!training || !userProfile) return;

        // Vérification de sécurité : ne jamais appeler le service de paiement pour les formations gratuites
        if (training.price === 0 || training.price <= 0) {
            handleDownloadFreeTraining();
            return;
        }

        setPaymentStatus(prev => ({ ...prev, isProcessing: true, message: null }));

        try {
            // Récupérer l'utilisateur actuel
            const currentUser = user?.uid ? user : auth.currentUser;

            if (!currentUser) {
                throw new Error("Vous devez être connecté pour effectuer un achat");
            }

            // Vérifier que l'email est disponible
            const userEmail = currentUser.email || userProfile.email;
            const userName = userProfile.name || currentUser.displayName || 'Utilisateur';

            if (!userEmail) {
                throw new Error("Email utilisateur manquant. Veuillez vous reconnecter.");
            }

            // Préparer les données pour le paiement
            const paymentData = await createSubscriptionPayment(
                {
                    uid: currentUser.uid,
                    email: userEmail,
                    name: userName,
                    userProfile: userProfile
                },
                {
                    id: 'training_' + training.id,
                    name: `Formation: ${training.title}`,
                    amount: training.price,
                    period: "unique"
                }
            );

            // Stocker l'information du paiement en attente
            localStorage.setItem('pendingTrainingPurchase', JSON.stringify({
                trainingId: training.id,
                timestamp: Date.now()
            }));

            // Mettre à jour l'état avec l'URL de paiement
            setPaymentStatus({
                isProcessing: false,
                paymentUrl: paymentData,
                success: false,
                message: null
            });

        } catch (error) {
            setPaymentStatus({
                isProcessing: false,
                paymentUrl: null,
                success: false,
                message: `Erreur: ${error.message}`
            });
        }
    };

    // Gérer le bouton d'action principal
    const handleMainAction = () => {
        // S'assurer qu'on n'appelle jamais le service de paiement pour les formations gratuites
        if (isFreeTraining) {
            handleDownloadFreeTraining();
        } else if (training.price > 0) {
            handlePurchasePaidTraining();
        } else {
            // Fallback : si le prix est exactement 0, traiter comme gratuit
            handleDownloadFreeTraining();
        }
    };

    // Formatage du prix
    const formatPrice = (price) => {
        if (price === 0) {
            return "Gratuit";
        }
        return `${new Intl.NumberFormat("fr-FR").format(price)} FCFA`;
    };

    // Afficher le texte du bouton approprié
    const getButtonText = () => {
        if (paymentStatus.isProcessing) {
            return "Traitement en cours...";
        }
        if (paymentStatus.success) {
            return isFreeTraining ? "Téléchargement en cours..." : "Achat réussi";
        }
        if (isFreeTraining) {
            return "Télécharger gratuitement";
        }
        return "Acquérir maintenant";
    };

    // Afficher l'icône du bouton appropriée
    const getButtonIcon = () => {
        if (paymentStatus.isProcessing) {
            return <div className="button-spinner"></div>;
        }
        if (paymentStatus.success) {
            return <i className="pe-7s-check"></i>;
        }
        if (isFreeTraining) {
            return <i className="pe-7s-download"></i>;
        }
        return <i className="pe-7s-cart"></i>;
    };

    // Retour à la liste des formations
    const handleBackToList = () => {
        history.push('/app/training');
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Chargement des informations...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="alert alert-danger">{error}</div>
                <button
                    onClick={handleBackToList}
                    className="btn btn-default"
                >
                    <i className="pe-7s-back"></i> Retour aux formations
                </button>
            </div>
        );
    }

    if (!training) {
        return (
            <div className="error-container">
                <div className="alert alert-warning">Formation introuvable</div>
                <button
                    onClick={handleBackToList}
                    className="btn btn-default"
                >
                    <i className="pe-7s-back"></i> Retour aux formations
                </button>
            </div>
        );
    }

    return (
        <div className="training-purchase-page">
            <div className="navigation-bar">
                <button
                    onClick={handleBackToList}
                    className="btn btn-default btn-sm back-button"
                >
                    <i className="pe-7s-back"></i> Retour aux formations
                </button>
            </div>

            <div className="purchase-container">
                <div className="training-summary">
                    <div className="training-image">
                        <i className={getContentTypeIcon(training.contentType)}></i>
                    </div>

                    <div className="training-details">
                        <h2 className="training-title">{training.title}</h2>

                        <div className="training-meta">
                            <div className="training-type">
                                <i className={getContentTypeIcon(training.contentType)}></i>
                                <span>{getContentTypeLabel(training.contentType)}</span>
                            </div>

                            <div className="training-category">
                                <i className={getCategoryIcon(training.category)}></i>
                                <span>{getCategoryLabel(training.category)}</span>
                            </div>
                        </div>

                        <div className="training-description">
                            <p>{training.summary}</p>
                        </div>

                        {/* Badge gratuit si applicable */}
                        {isFreeTraining && (
                            <div className="free-badge">
                                <i className="pe-7s-gift"></i>
                                <span>Formation 100% Gratuite</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="purchase-summary">
                    <h3>Résumé de la commande</h3>

                    <div className="purchase-item">
                        <span>Formation</span>
                        <span>{training.title}</span>
                    </div>

                    <div className="purchase-item">
                        <span>Prix</span>
                        <span className={`price ${isFreeTraining ? 'free' : 'paid'}`}>
                            {formatPrice(training.price)}
                        </span>
                    </div>

                    <div className="purchase-total">
                        <span>Total</span>
                        <span className={`total-price ${isFreeTraining ? 'free' : 'paid'}`}>
                            {formatPrice(training.price)}
                        </span>
                    </div>

                    {paymentStatus.message && (
                        <div className={`alert ${paymentStatus.success ? 'alert-success' : 'alert-danger'}`}>
                            {paymentStatus.message}
                        </div>
                    )}

                    {paymentStatus.paymentUrl ? (
                        <div className="payment-redirect">
                            <p>Vous allez être redirigé vers notre plateforme de paiement sécurisée Ligdicash.</p>
                            <a
                                href={paymentStatus.paymentUrl}
                                className="btn btn-primary btn-fill btn-block"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="pe-7s-credit"></i> Procéder au paiement Ligdicash
                            </a>
                        </div>
                    ) : (
                        <button
                            onClick={handleMainAction}
                            className={`btn btn-fill btn-block purchase-button ${
                                isFreeTraining ? 'btn-info' : 'btn-success'
                            }`}
                            disabled={paymentStatus.isProcessing || paymentStatus.success}
                        >
                            {getButtonIcon()}
                            {getButtonText()}
                        </button>
                    )}

                    {!isFreeTraining && (
                        <div className="secure-payment">
                            <i className="pe-7s-lock"></i>
                            <span>Paiement 100% sécurisé</span>
                        </div>
                    )}

                    {isFreeTraining && (
                        <div className="free-benefits">
                            <h4>🎁 Cette formation est offerte :</h4>
                            <ul>
                                <li>✅ Téléchargement immédiat du PDF</li>
                                <li>✅ Accès permanent</li>
                                <li>✅ Aucun paiement requis</li>
                                <li>✅ Support inclus</li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .training-purchase-page {
                    min-height: 100vh;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    padding: 20px;
                }

                .navigation-bar {
                    padding: 20px;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .back-button {
                    background: rgba(255, 255, 255, 0.95);
                    border: none;
                    border-radius: 12px;
                    padding: 12px 24px;
                    font-weight: 600;
                    color: #4a5568;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
                }

                .back-button:hover {
                    background: white;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
                }

                .purchase-container {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 40px;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .training-summary {
                    flex: 1;
                    min-width: 350px;
                    background: rgba(255, 255, 255, 0.95);
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .training-image {
                    height: 200px;
                    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    overflow: hidden;
                }

                .training-image::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
                }

                .training-image i {
                    font-size: 4rem;
                    color: white;
                    z-index: 2;
                    position: relative;
                    filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
                }

                .training-details {
                    padding: 30px;
                }

                .training-title {
                    margin: 0 0 20px 0;
                    color: #2d3748;
                    font-size: 1.8rem;
                    font-weight: 800;
                    line-height: 1.3;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .training-meta {
                    display: flex;
                    gap: 15px;
                    margin-bottom: 25px;
                    flex-wrap: wrap;
                }

                .training-type, .training-category {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    padding: 10px 16px;
                    border-radius: 12px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #4a5568;
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s ease;
                }

                .training-type:hover, .training-category:hover {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    transform: translateY(-2px);
                }

                .training-description {
                    margin-bottom: 20px;
                }

                .training-description p {
                    color: #4a5568;
                    line-height: 1.7;
                    font-size: 1.1rem;
                    margin: 0;
                }

                .free-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
                    color: white;
                    padding: 12px 20px;
                    border-radius: 25px;
                    font-weight: 700;
                    font-size: 1rem;
                    box-shadow: 0 8px 25px rgba(72, 187, 120, 0.3);
                    animation: pulse 2s infinite;
                }

                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                .purchase-summary {
                    width: 400px;
                    background: rgba(255, 255, 255, 0.98);
                    border-radius: 20px;
                    padding: 30px;
                    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    height: fit-content;
                    position: sticky;
                    top: 20px;
                }

                .purchase-summary h3 {
                    margin: 0 0 25px 0;
                    color: #2d3748;
                    font-size: 1.5rem;
                    font-weight: 800;
                    padding-bottom: 15px;
                    border-bottom: 2px solid #e2e8f0;
                    text-align: center;
                }

                .purchase-item {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 18px;
                    font-size: 1rem;
                    color: #4a5568;
                    padding: 10px 0;
                    border-bottom: 1px solid #f7fafc;
                }

                .purchase-item span:first-child {
                    font-weight: 600;
                }

                .purchase-item span:last-child {
                    font-weight: 700;
                }

                .purchase-total {
                    display: flex;
                    justify-content: space-between;
                    margin: 25px 0;
                    padding: 20px;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    border-radius: 15px;
                    font-weight: 800;
                    font-size: 1.2rem;
                    color: #2d3748;
                    border: 2px solid #e2e8f0;
                }

                .price.free, .total-price.free {
                    color: #48bb78;
                    font-weight: 800;
                    font-size: 1.3rem;
                    text-shadow: 0 2px 4px rgba(72, 187, 120, 0.2);
                }

                .price.paid, .total-price.paid {
                    color: #e53e3e;
                    font-weight: 800;
                }

                .total-price.paid {
                    font-size: 1.4rem;
                }

                .purchase-button {
                    margin: 25px 0;
                    height: 60px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    font-size: 1.1rem;
                    font-weight: 700;
                    border-radius: 15px;
                    border: none;
                    transition: all 0.3s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .btn-info {
                    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                    box-shadow: 0 8px 25px rgba(79, 172, 254, 0.4);
                }

                .btn-info:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 15px 35px rgba(79, 172, 254, 0.6);
                    background: linear-gradient(135deg, #00f2fe 0%, #4facfe 100%);
                }

                .btn-success {
                    background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
                    box-shadow: 0 8px 25px rgba(72, 187, 120, 0.4);
                }

                .btn-success:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 15px 35px rgba(72, 187, 120, 0.6);
                    background: linear-gradient(135deg, #38a169 0%, #48bb78 100%);
                }

                .btn-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
                }

                .btn-primary:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 15px 35px rgba(102, 126, 234, 0.6);
                    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
                }

                .purchase-button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                    transform: none !important;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
                }

                .payment-redirect {
                    margin: 25px 0;
                    text-align: center;
                    padding: 20px;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    border-radius: 15px;
                    border: 2px solid #e2e8f0;
                }

                .payment-redirect p {
                    margin-bottom: 20px;
                    font-size: 1rem;
                    color: #4a5568;
                    line-height: 1.6;
                    font-weight: 500;
                }

                .button-spinner {
                    width: 24px;
                    height: 24px;
                    border: 3px solid rgba(255,255,255,0.3);
                    border-radius: 50%;
                    border-top-color: white;
                    animation: spin 1s linear infinite;
                }

                .secure-payment {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    margin-top: 25px;
                    color: #4a5568;
                    font-size: 1rem;
                    font-weight: 600;
                    padding: 15px;
                    background: linear-gradient(135deg, #f0fff4 0%, #f0fff4 100%);
                    border-radius: 12px;
                    border: 1px solid #9ae6b4;
                }

                .secure-payment i {
                    color: #48bb78;
                    font-size: 1.2rem;
                }

                .free-benefits {
                    margin-top: 25px;
                    padding: 25px;
                    background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
                    border-radius: 15px;
                    border: 2px solid #9ae6b4;
                }

                .free-benefits h4 {
                    margin: 0 0 18px 0;
                    color: #2d3748;
                    font-size: 1.2rem;
                    font-weight: 800;
                    text-align: center;
                }

                .free-benefits ul {
                    margin: 0;
                    padding-left: 0;
                    color: #4a5568;
                    list-style: none;
                }

                .free-benefits li {
                    margin-bottom: 12px;
                    line-height: 1.5;
                    font-weight: 500;
                    font-size: 1rem;
                }

                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100vh;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                }

                .spinner {
                    border: 4px solid rgba(255, 255, 255, 0.2);
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    border-left-color: white;
                    animation: spin 1s linear infinite;
                    margin-bottom: 25px;
                }

                .loading-container p {
                    font-size: 1.2rem;
                    font-weight: 600;
                    margin: 0;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .error-container {
                    padding: 60px 40px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    min-height: 100vh;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    justify-content: center;
                }

                .error-container .alert {
                    margin-bottom: 30px;
                    width: 100%;
                    max-width: 600px;
                    padding: 25px;
                    border-radius: 15px;
                    font-size: 1.1rem;
                    font-weight: 600;
                    text-align: center;
                    background: rgba(255, 255, 255, 0.95);
                    color: #e53e3e;
                    border: 2px solid #feb2b2;
                    backdrop-filter: blur(10px);
                }

                .alert-success {
                    background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
                    color: #22543d;
                    border: 2px solid #9ae6b4;
                    padding: 20px;
                    border-radius: 12px;
                    text-align: center;
                    font-weight: 600;
                    margin: 20px 0;
                }

                .alert-danger {
                    background: linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%);
                    color: #742a2a;
                    border: 2px solid #fc8181;
                    padding: 20px;
                    border-radius: 12px;
                    text-align: center;
                    font-weight: 600;
                    margin: 20px 0;
                }

                @media (max-width: 768px) {
                    .training-purchase-page {
                        padding: 15px;
                    }

                    .purchase-container {
                        flex-direction: column;
                        gap: 25px;
                    }

                    .purchase-summary {
                        width: 100%;
                        position: static;
                    }

                    .training-summary {
                        min-width: auto;
                    }

                    .training-meta {
                        flex-direction: column;
                        gap: 10px;
                    }

                    .training-title {
                        font-size: 1.5rem;
                    }

                    .training-details {
                        padding: 20px;
                    }

                    .purchase-summary {
                        padding: 20px;
                    }

                    .navigation-bar {
                        padding: 15px;
                    }
                }

                @media (max-width: 480px) {
                    .training-title {
                        font-size: 1.3rem;
                    }

                    .purchase-summary h3 {
                        font-size: 1.3rem;
                    }

                    .purchase-button {
                        height: 55px;
                        font-size: 1rem;
                    }

                    .training-image {
                        height: 150px;
                    }

                    .training-image i {
                        font-size: 3rem;
                    }
                }
            `}</style>
        </div>
    );
};

TrainingPurchasePage.propTypes = {
    user: PropTypes.shape({
        uid: PropTypes.string,
        email: PropTypes.string,
        displayName: PropTypes.string
    }),
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired
};

export default withRouter(TrainingPurchasePage);